/**
 * Shared update types for Electron auto-updater
 * Safe for import in both main and renderer processes
 */

export interface UpdateInfo {
  version: string;
  releaseDate?: string;
  releaseNotes?: string;
}

export interface UpdateProgress {
  bytesPerSecond: number;
  percent: number;
  total: number;
  transferred: number;
}

export interface UpdateStatus {
  isChecking: boolean;
  updateAvailable: boolean;
  isDownloading: boolean;
  isDownloaded: boolean;
  currentVersion: string;
  availableVersion?: string;
  downloadProgress?: UpdateProgress;
  releaseNotes?: string;
  error?: string;
}

export type UpdateChannel = 'alpha' | 'beta' | 'latest';

export interface ChannelVersionsData {
  channels: {
    latest: { current_version: string; history: Array<{ version: string }> };
    beta: { current_version: string; history: Array<{ version: string }> };
    alpha: { current_version: string; history: Array<{ version: string }> };
  };
}

export interface UpdateResult {
  success: boolean;
  error?: string;
  updateInfo?: UpdateInfo;
}

export type UpdateActivityKind =
  | 'chat'
  | 'voice'
  | 'speech'
  | 'transcription'
  | 'meeting'
  | 'postProcessing';

export interface UpdateActivityReport {
  activeKinds: UpdateActivityKind[];
  requestGeneration?: number;
}

export interface UpdateActivityRequest {
  generation: number;
}

// Update event types for IPC communication (Electron internal)
export enum UpdateEventType {
  // IPC handler channels
  CheckForUpdates = 'updater:check-for-updates',
  StartDownload = 'updater:start-download',
  QuitAndInstall = 'updater:quit-and-install',
  ReportActivity = 'updater:report-activity',
  GetStatus = 'updater:get-status',
  GetCurrentVersionChannelInfo = 'updater:get-current-version-channel-info',
  GetUserSubscribedChannel = 'updater:get-user-subscribed-channel',
  GetInstallHealth = 'updater:get-install-health',
  RepairInstall = 'updater:repair-install',
  DismissInstallRepair = 'updater:dismiss-install-repair',

  // Event channels (main -> renderer)
  UpdateChecking = 'update-checking',
  UpdateAvailable = 'update-available',
  UpdateNotAvailable = 'update-not-available',
  UpdateDownloadProgress = 'update-download-progress',
  UpdateDownloaded = 'update-downloaded',
  UpdateError = 'update-error',
  UpdateStatusChanged = 'update-status-changed',
  InstallHealthChanged = 'install-health-changed',
  RequestActivityReport = 'update-request-activity-report',
}

export type UpdateEventChannel =
  | UpdateEventType.UpdateChecking
  | UpdateEventType.UpdateAvailable
  | UpdateEventType.UpdateNotAvailable
  | UpdateEventType.UpdateDownloadProgress
  | UpdateEventType.UpdateDownloaded
  | UpdateEventType.UpdateError;

export type InstallHealthIssue =
  | 'wrongLocation'
  | 'wrongOwner'
  | 'parentUnwritable';

export type InstallRepairKind = 'moveToApplications' | 'reinstall' | 'none';

export interface InstallHealthStatus {
  healthy: boolean;
  issues: InstallHealthIssue[];
  repair: InstallRepairKind;
  shouldPrompt: boolean;
  bundlePath: string;
  appVersion: string;
}

export interface InstallRepairResult {
  success: boolean;
  repair: InstallRepairKind;
  error?: string;
}
